/**
 * Generated by orval v7.7.0 🍺
 * Do not edit manually.
 * GTO Wizard API v4
 * API documentation for GTO Wizard frontend API
 * OpenAPI spec version: 4
 */
import { useMutation, useQuery } from '@tanstack/vue-query'
import type {
  DataTag,
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseMutationReturnType,
  UseQueryOptions,
  UseQueryReturnType,
} from '@tanstack/vue-query'

import { computed, unref } from 'vue'
import type { MaybeRef } from 'vue'

import type {
  ChipDistributionGraphSchema,
  ErrorResponse,
  GetChipDistributionParams,
  GetPredefinedTournamentsParams,
  PagedSavedTournamentSchema,
  SavedTournamentWithStructureSchema,
  SearchTournament429,
  TournamentSearchRequest,
} from '../models'

import getPredefinedTournamentsMutator from '../../../client'
import getTournamentMutator from '../../../client'
import searchTournamentMutator from '../../../client'
import getChipDistributionMutator from '../../../client'

type AwaitedInput<T> = PromiseLike<T> | T

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never

/**
 * Gets all predefined tournaments. Predefined tournament is a tournament prepared by GTOWizard for all players to see and use.
 * @summary Get Predefined Tournaments
 */
export const getPredefinedTournaments = (
  params?: MaybeRef<GetPredefinedTournamentsParams>,
  signal?: AbortSignal,
) => {
  params = unref(params)

  return getPredefinedTournamentsMutator<PagedSavedTournamentSchema>({
    url: `/v4/tournaments/predefined/`,
    method: 'GET',
    params: unref(params),
    signal,
  })
}

export const getGetPredefinedTournamentsQueryKey = (
  params?: MaybeRef<GetPredefinedTournamentsParams>,
) => {
  return ['v4', 'tournaments', 'predefined', ...(params ? [params] : [])] as const
}

export const getGetPredefinedTournamentsQueryOptions = <
  TData = Awaited<ReturnType<typeof getPredefinedTournaments>>,
  TError = unknown,
>(
  params?: MaybeRef<GetPredefinedTournamentsParams>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPredefinedTournaments>>, TError, TData>
    >
  },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = getGetPredefinedTournamentsQueryKey(params)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getPredefinedTournaments>>> = ({
    signal,
  }) => getPredefinedTournaments(params, signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getPredefinedTournaments>>,
    TError,
    TData
  >
}

export type GetPredefinedTournamentsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getPredefinedTournaments>>
>
export type GetPredefinedTournamentsQueryError = unknown

/**
 * @summary Get Predefined Tournaments
 */

export function useGetPredefinedTournaments<
  TData = Awaited<ReturnType<typeof getPredefinedTournaments>>,
  TError = unknown,
>(
  params?: MaybeRef<GetPredefinedTournamentsParams>,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getPredefinedTournaments>>, TError, TData>
    >
  },
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetPredefinedTournamentsQueryOptions(params, options)

  const query = useQuery(queryOptions) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>
  }

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>

  return query
}

/**
 * Gets any tournament by id - should be safe as it is UUID
 * @summary Get Tournament
 */
export const getTournament = (id: MaybeRef<string>, signal?: AbortSignal) => {
  id = unref(id)

  return getTournamentMutator<SavedTournamentWithStructureSchema>({
    url: `/v4/tournaments/${id}/`,
    method: 'GET',
    signal,
  })
}

export const getGetTournamentQueryKey = (id: MaybeRef<string>) => {
  return ['v4', 'tournaments', id] as const
}

export const getGetTournamentQueryOptions = <
  TData = Awaited<ReturnType<typeof getTournament>>,
  TError = ErrorResponse,
>(
  id: MaybeRef<string>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTournament>>, TError, TData>>
  },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = getGetTournamentQueryKey(id)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getTournament>>> = ({ signal }) =>
    getTournament(id, signal)

  return {
    queryKey,
    queryFn,
    enabled: computed(() => !!unref(id)),
    ...queryOptions,
  } as UseQueryOptions<Awaited<ReturnType<typeof getTournament>>, TError, TData>
}

export type GetTournamentQueryResult = NonNullable<Awaited<ReturnType<typeof getTournament>>>
export type GetTournamentQueryError = ErrorResponse

/**
 * @summary Get Tournament
 */

export function useGetTournament<
  TData = Awaited<ReturnType<typeof getTournament>>,
  TError = ErrorResponse,
>(
  id: MaybeRef<string>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getTournament>>, TError, TData>>
  },
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetTournamentQueryOptions(id, options)

  const query = useQuery(queryOptions) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>
  }

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>

  return query
}

/**
 * Searches tournament in Sharkscope and saves it so it doesn't have to be imported every time.
If tournament already exists we just return tournament from DB
 * @summary Search Tournament
 */
export const searchTournament = (
  tournamentSearchRequest: MaybeRef<TournamentSearchRequest>,
  signal?: AbortSignal,
) => {
  tournamentSearchRequest = unref(tournamentSearchRequest)

  return searchTournamentMutator<SavedTournamentWithStructureSchema>({
    url: `/v4/tournaments/search/`,
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    data: tournamentSearchRequest,
    signal,
  })
}

export const getSearchTournamentMutationOptions = <
  TError = ErrorResponse | SearchTournament429,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchTournament>>,
    TError,
    { data: TournamentSearchRequest },
    TContext
  >
}): UseMutationOptions<
  Awaited<ReturnType<typeof searchTournament>>,
  TError,
  { data: TournamentSearchRequest },
  TContext
> => {
  const mutationKey = ['searchTournament']
  const { mutation: mutationOptions } = options
    ? options.mutation && 'mutationKey' in options.mutation && options.mutation.mutationKey
      ? options
      : { ...options, mutation: { ...options.mutation, mutationKey } }
    : { mutation: { mutationKey } }

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof searchTournament>>,
    { data: TournamentSearchRequest }
  > = props => {
    const { data } = props ?? {}

    return searchTournament(data)
  }

  return { mutationFn, ...mutationOptions }
}

export type SearchTournamentMutationResult = NonNullable<
  Awaited<ReturnType<typeof searchTournament>>
>
export type SearchTournamentMutationBody = TournamentSearchRequest
export type SearchTournamentMutationError = ErrorResponse | SearchTournament429

/**
 * @summary Search Tournament
 */
export const useSearchTournament = <
  TError = ErrorResponse | SearchTournament429,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof searchTournament>>,
    TError,
    { data: TournamentSearchRequest },
    TContext
  >
}): UseMutationReturnType<
  Awaited<ReturnType<typeof searchTournament>>,
  TError,
  { data: TournamentSearchRequest },
  TContext
> => {
  const mutationOptions = getSearchTournamentMutationOptions(options)

  return useMutation(mutationOptions)
}
/**
 * Calculates distribution of chips in the tournament for remaining players
 * @summary Get Chip Distribution
 */
export const getChipDistribution = (
  params: MaybeRef<GetChipDistributionParams>,
  signal?: AbortSignal,
) => {
  params = unref(params)

  return getChipDistributionMutator<ChipDistributionGraphSchema>({
    url: `/v4/tournaments/chip-distribution/`,
    method: 'GET',
    params: unref(params),
    signal,
  })
}

export const getGetChipDistributionQueryKey = (params: MaybeRef<GetChipDistributionParams>) => {
  return ['v4', 'tournaments', 'chip-distribution', ...(params ? [params] : [])] as const
}

export const getGetChipDistributionQueryOptions = <
  TData = Awaited<ReturnType<typeof getChipDistribution>>,
  TError = unknown,
>(
  params: MaybeRef<GetChipDistributionParams>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChipDistribution>>, TError, TData>>
  },
) => {
  const { query: queryOptions } = options ?? {}

  const queryKey = getGetChipDistributionQueryKey(params)

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getChipDistribution>>> = ({ signal }) =>
    getChipDistribution(params, signal)

  return { queryKey, queryFn, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getChipDistribution>>,
    TError,
    TData
  >
}

export type GetChipDistributionQueryResult = NonNullable<
  Awaited<ReturnType<typeof getChipDistribution>>
>
export type GetChipDistributionQueryError = unknown

/**
 * @summary Get Chip Distribution
 */

export function useGetChipDistribution<
  TData = Awaited<ReturnType<typeof getChipDistribution>>,
  TError = unknown,
>(
  params: MaybeRef<GetChipDistributionParams>,
  options?: {
    query?: Partial<UseQueryOptions<Awaited<ReturnType<typeof getChipDistribution>>, TError, TData>>
  },
): UseQueryReturnType<TData, TError> & { queryKey: DataTag<QueryKey, TData, TError> } {
  const queryOptions = getGetChipDistributionQueryOptions(params, options)

  const query = useQuery(queryOptions) as UseQueryReturnType<TData, TError> & {
    queryKey: DataTag<QueryKey, TData, TError>
  }

  query.queryKey = unref(queryOptions).queryKey as DataTag<QueryKey, TData, TError>

  return query
}
